<template>
  <div class="proyectos-detalle">
    <!-- Header -->
    <div class="content-header">
      <div class="content-header-left">
        <h1 class="content-header-title">{{ proyecto.nombre || 'Cargando...' }}</h1>
        <p class="content-header-subtitle">{{ proyecto.descripcion || '' }}</p>
      </div>
      <div class="content-header-right">
        <b-button-group>
          <b-button variant="outline-warning" @click="editarProyecto">
            <feather-icon icon="EditIcon" size="16" class="mr-50" />
            Editar
          </b-button>
          <b-button variant="outline-secondary" @click="volver">
            <feather-icon icon="ArrowLeftIcon" size="16" class="mr-50" />
            Volver
          </b-button>
        </b-button-group>
      </div>
    </div>

    <div v-if="cargando" class="text-center py-5">
      <b-spinner class="text-primary"></b-spinner>
      <p class="mt-2">Cargando proyecto...</p>
    </div>

    <div v-else>
      <!-- Información General -->
      <b-row>
        <b-col lg="8">
          <b-card title="Información General" class="mb-4">
            <b-row>
              <b-col md="6">
                <div class="info-item">
                  <label>Estado:</label>
                  <b-badge :variant="getEstadoVariant(proyecto.nombreEstadoProyecto)" class="text-capitalize">
                    {{ proyecto.nombreEstadoProyecto || 'Sin estado' }}
                  </b-badge>
                </div>
              </b-col>
              <b-col md="6">
                <div class="info-item">
                  <label>Progreso:</label>
                  <div class="d-flex align-items-center">
                    <b-progress
                      :value="calcularProgreso()"
                      :max="100"
                      class="flex-grow-1 mr-2"
                      height="8px"
                    />
                    <span class="text-muted">{{ calcularProgreso() }}%</span>
                  </div>
                </div>
              </b-col>
            </b-row>

            <b-row class="mt-3">
              <b-col md="6">
                <div class="info-item">
                  <label>Fecha de Inicio:</label>
                  <span>{{ formatearFecha(proyecto.fechaInicio) }}</span>
                </div>
              </b-col>
              <b-col md="6">
                <div class="info-item">
                  <label>Fecha de Fin:</label>
                  <span>{{ formatearFecha(proyecto.fechaFin) }}</span>
                </div>
              </b-col>
            </b-row>

            <b-row class="mt-3">
              <b-col md="6">
                <div class="info-item">
                  <label>Presupuesto:</label>
                  <span>{{ formatearMoneda(proyecto.presupuesto) }}</span>
                </div>
              </b-col>
              <b-col md="6">
                <div class="info-item">
                  <label>Responsable:</label>
                  <span>{{ proyecto.nombreResponsable || 'Sin responsable' }}</span>
                </div>
              </b-col>
            </b-row>

            <div class="info-item mt-3">
              <label>Descripción:</label>
              <p class="mt-1">{{ proyecto.descripcion || 'Sin descripción' }}</p>
            </div>
          </b-card>

          <!-- Actividades del Proyecto -->
          <b-card title="Actividades" class="mb-4">
            <b-table
              :items="actividades"
              :fields="actividadesFields"
              responsive
              striped
              hover
            >
              <template #cell(estadoActividad.nombre)="data">
                <b-badge
                  :variant="getEstadoVariant(data.item.estadoActividad?.nombre)"
                  class="text-capitalize"
                >
                  {{ data.item.estadoActividad?.nombre || 'Sin estado' }}
                </b-badge>
              </template>
              <template #cell(acciones)="data">
                <b-button-group size="sm">
                  <b-button
                    variant="outline-primary"
                    @click="verActividad(data.item.idActividad)"
                  >
                    Ver
                  </b-button>
                  <b-button
                    variant="outline-warning"
                    @click="editarActividad(data.item)"
                    v-b-tooltip.hover
                    title="Editar actividad"
                  >
                    <feather-icon icon="EditIcon" size="14" />
                  </b-button>
                  <b-button
                    variant="outline-info"
                    @click="agregarRecordatorio(data.item)"
                    v-b-tooltip.hover
                    title="Agregar recordatorio al calendario"
                  >
                    <feather-icon icon="CalendarIcon" size="14" />
                  </b-button>
                </b-button-group>
              </template>
            </b-table>
          </b-card>

          <!-- Obras del Proyecto -->
          <b-card title="Obras" class="mb-4">
            <div v-if="obras.length === 0" class="text-center text-muted py-3">
              <feather-icon icon="BuildingIcon" size="24" class="mb-2" />
              <p>No hay obras asociadas a este proyecto</p>
            </div>
            <b-table
              v-else
              :items="obras"
              :fields="obrasFields"
              responsive
              striped
              hover
            >
              <template #cell(nombreEstadoObra)="data">
                <b-badge
                  :variant="getEstadoObraVariant(data.item.nombreEstadoObra)"
                  class="text-capitalize"
                >
                  {{ data.item.nombreEstadoObra || 'Sin estado' }}
                </b-badge>
              </template>
              <template #cell(fechaInicioObra)="data">
                {{ formatearFecha(data.item.fechaInicioObra) }}
              </template>
              <template #cell(acciones)="data">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="verObra(data.item.idObra)"
                >
                  Ver
                </b-button>
              </template>
            </b-table>
          </b-card>
        </b-col>

        <!-- Sidebar -->
        <b-col lg="4">
          <!-- Estadísticas -->
          <b-card title="Estadísticas" class="mb-4">
            <div class="stats-item">
              <div class="stats-value">{{ estadisticas.actividadesCompletadas }}</div>
              <div class="stats-label">Actividades Completadas</div>
            </div>
            <div class="stats-item">
              <div class="stats-value">{{ estadisticas.actividadesPendientes }}</div>
              <div class="stats-label">Actividades Pendientes</div>
            </div>
            <div class="stats-item">
              <div class="stats-value">{{ estadisticas.diasRestantes }}</div>
              <div class="stats-label">Días Restantes</div>
            </div>
          </b-card>

          <!-- Archivos -->
          <b-card title="Archivos" class="mb-4">
            <div v-if="archivos.length === 0" class="text-center text-muted py-3">
              <feather-icon icon="FileIcon" size="24" class="mb-2" />
              <p>No hay archivos adjuntos</p>
            </div>
            <div v-else>
              <div
                v-for="archivo in archivos"
                :key="archivo.idDocumento || archivo.id"
                class="file-item"
              >
                <div class="file-info">
                  <div class="file-name">
                    <feather-icon icon="FileIcon" size="16" class="mr-2" />
                    {{ archivo.nombreArchivo || archivo.nombre }}
                  </div>
                  <div class="file-meta">
                    <small class="text-muted">
                      <span v-if="archivo.actividadNombre">Actividad: {{ archivo.actividadNombre }}</span>
                      <span v-if="archivo.nombreCategoriaArchivo"> | Categoría: {{ archivo.nombreCategoriaArchivo }}</span>
                      <span v-if="archivo.version"> | v{{ archivo.version }}</span>
                      <span v-if="archivo.actividadFecha"> | {{ formatearFecha(archivo.actividadFecha) }}</span>
                    </small>
                  </div>
                </div>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="descargarArchivo(archivo.idDocumento || archivo.id)"
                >
                  <feather-icon icon="DownloadIcon" size="14" />
                </b-button>
              </div>
            </div>
          </b-card>

          <!-- Observaciones de Actividades -->
          <b-card title="Observaciones de Actividades">
            <div v-if="observacionesActividades.length === 0" class="text-center text-muted py-3">
              <feather-icon icon="MessageSquareIcon" size="24" class="mb-2" />
              <p>No hay observaciones en las actividades</p>
            </div>
            <div v-else>
              <div
                v-for="observacion in observacionesActividades"
                :key="observacion.id"
                class="comment-item"
              >
                <div class="comment-header">
                  <strong>{{ observacion.tipoActividad }}</strong>
                  <small class="text-muted">{{ formatearFecha(observacion.fecha) }}</small>
                </div>
                <p class="comment-text">{{ observacion.observaciones }}</p>
                <div class="comment-meta">
                  <b-badge :variant="getEstadoVariant(observacion.estado)" class="text-capitalize">
                    {{ observacion.estado }}
                  </b-badge>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Modal para Editar Actividad -->
    <b-modal
      id="modal-editar-actividad"
      title="Editar Actividad"
      size="lg"
      @hidden="resetModalActividad"
    >
      <b-form @submit.prevent="guardarActividadEditada">
        <b-row>
          <b-col md="6">
            <b-form-group label="Tipo de Actividad" required>
              <b-form-select v-model="modalActividad.tipoActividad" :options="opcionesTipoActividad" required />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Fecha" required>
              <b-form-datepicker 
                v-model="modalActividad.fecha" 
                :start-weekday="1"
                required 
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Estado de Actividad" required>
              <b-form-select v-model="modalActividad.estadoActividad" :options="opcionesEstadoActividad" required />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Próxima Acción Fecha">
              <b-form-datepicker 
                v-model="modalActividad.proximaAccionFecha" 
                :start-weekday="1"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Próxima Acción Tipo">
              <b-form-select v-model="modalActividad.proximaAccionTipo" :options="opcionesAccionActividad" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="12">
            <b-form-group label="Observaciones">
              <b-form-textarea
                v-model="modalActividad.observaciones"
                rows="3"
                placeholder="Ingrese observaciones sobre la actividad..."
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group>
              <b-form-checkbox v-model="modalActividad.recordatorio">
                Activar recordatorio
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Sección de Documentos -->
        <b-row>
          <b-col md="12">
            <b-form-group label="Documentos Adjuntos">
              <div v-if="documentosActividad.length === 0" class="text-center text-muted py-3">
                <feather-icon icon="FileIcon" size="24" class="mb-2" />
                <p>No hay documentos adjuntos</p>
              </div>
              <div v-else>
                <div
                  v-for="(documento, index) in documentosActividad"
                  :key="index"
                  class="documento-item d-flex justify-content-between align-items-center mb-2 p-2 border rounded"
                >
                  <div class="documento-info">
                    <div class="documento-nombre">
                      <feather-icon icon="FileIcon" size="16" class="mr-2" />
                      {{ documento.nombreArchivo }}
                    </div>
                    <small class="text-muted">
                      Categoría: {{ documento.nombreCategoriaArchivo || 'Sin categoría' }} | 
                      Versión: {{ documento.version || '1.0' }}
                    </small>
                  </div>
                  <div class="d-flex gap-1">
                    <b-button
                      v-if="documento.idDocumento"
                      variant="outline-primary"
                      size="sm"
                      @click="descargarDocumento(documento.idDocumento, documento.nombreArchivo)"
                      v-b-tooltip.hover
                      title="Descargar documento"
                    >
                      <feather-icon icon="DownloadIcon" size="14" />
                    </b-button>
                    <b-button
                      variant="outline-danger"
                      size="sm"
                      @click="eliminarDocumento(index)"
                      v-b-tooltip.hover
                      title="Eliminar documento"
                    >
                      <feather-icon icon="TrashIcon" size="14" />
                    </b-button>
                  </div>
                </div>
              </div>
              
              <div class="d-flex gap-2 mt-2">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="mostrarSubidaDocumentos = true"
                >
                  <feather-icon icon="PlusIcon" size="14" class="mr-1" />
                  Agregar Documento
                </b-button>
                <b-button
                  variant="outline-info"
                  size="sm"
                  @click="recargarDocumentos"
                >
                  <feather-icon icon="RefreshCwIcon" size="14" class="mr-1" />
                  Recargar
                </b-button>
              </div>
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>

      <template #modal-footer>
        <b-button variant="secondary" @click="cancelarEdicion">
          Cancelar
        </b-button>
        <b-button 
          variant="primary" 
          @click="guardarActividadEditada"
          :disabled="guardandoActividad"
        >
          <b-spinner v-if="guardandoActividad" small class="mr-1"></b-spinner>
          {{ guardandoActividad ? 'Guardando...' : 'Guardar Cambios' }}
        </b-button>
      </template>
    </b-modal>

    <!-- Modal para Subir Documentos -->
    <b-modal
      id="modal-subir-documento"
      title="Agregar Documento"
      size="lg"
      v-model="mostrarSubidaDocumentos"
      @hidden="resetSubidaDocumentos"
    >
      <SubidaDocumentos
        :id-actividad="actividadEditando?.idActividad"
        :id-usuario="usuarioActual.idUsuario"
        :modo-temporal="false"
        @documento-subido="onDocumentoSubido"
        @finalizar-subida="onFinalizarSubida"
      />
    </b-modal>
  </div>
</template>

<script>
import { ProyectoService } from '@/services/proyectoService'
import { DocumentoService } from '@/services/documentoService'
import { ActividadService } from '@/services/actividadService'
import { CategoriaArchivoService } from '@/services/categoriaArchivoService'
import SubidaDocumentos from '@/components/SubidaDocumentos.vue'
import authService from '@/services/authService'
import { 
  generarEnlaceGoogleCalendar, 
  generarEnlaceOutlookCalendar, 
  generarEnlaceYahooCalendar, 
  descargarArchivoICS,
  crearEventoDesdeActividad 
} from '@/utils/calendarUtils'

export default {
  name: 'ProyectosDetalle',
  data() {
    return {
      cargando: true,
      proyecto: {},
      actividadesFields: [
        { key: 'tipoActividad.nombre', label: 'Tipo' },
        { key: 'estadoActividad.nombre', label: 'Estado' },
        { key: 'fecha', label: 'Fecha' },
        { key: 'acciones', label: 'Acciones' },
      ],
      actividades: [],
      obrasFields: [
        { key: 'direccion', label: 'Dirección', sortable: true },
        { key: 'nombreTipoObra', label: 'Tipo', sortable: true },
        { key: 'nombreEstadoObra', label: 'Estado', sortable: true },
        { key: 'fechaInicioObra', label: 'Fecha Inicio', sortable: true },
        { key: 'acciones', label: 'Acciones' },
      ],
      obras: [],
      estadisticas: {
        actividadesCompletadas: 0,
        actividadesPendientes: 0,
        diasRestantes: 0,
      },
      archivos: [],
      observacionesActividades: [],
      // Variables para edición de actividad
      modalActividad: {
        tipoActividad: null,
        fecha: '',
        estadoActividad: null,
        observaciones: '',
        proximaAccionFecha: '',
        proximaAccionTipo: null,
        recordatorio: false,
      },
      documentosActividad: [],
      actividadEditando: null,
      guardandoActividad: false,
      mostrarSubidaDocumentos: false,
      // Opciones para los campos select
      opcionesTipoActividad: [
        { value: null, text: 'Seleccionar tipo' }
      ],
      opcionesEstadoActividad: [
        { value: null, text: 'Seleccionar estado' }
      ],
      opcionesAccionActividad: [
        { value: null, text: 'Seleccionar acción' }
      ],
      usuarioActual: {
        idUsuario: null
      },
    }
  },
  components: {
    SubidaDocumentos
  },
  mounted() {
    this.cargarProyecto()
    this.cargarOpcionesSelect()
    this.cargarUsuarioActual()
  },
  methods: {
    cargarUsuarioActual() {
      const usuarioAutenticado = authService.getCurrentUser()
      if (usuarioAutenticado) {
        const idUsuarioAutenticado = usuarioAutenticado.idUsuario || usuarioAutenticado.id
        if (idUsuarioAutenticado) {
          this.usuarioActual = {
            idUsuario: idUsuarioAutenticado
          }
        }
      }
    },
    async cargarProyecto() {
      this.cargando = true
      try {
        const proyectoId = parseInt(this.$route.params.id)
        
        // Cargar datos del proyecto desde el backend
        this.proyecto = await ProyectoService.getById(proyectoId)
        
        // Cargar actividades del proyecto
        this.actividades = await ProyectoService.getActividadesByProyecto(proyectoId)
        
        // Cargar obras del proyecto
        this.obras = await ProyectoService.getObrasByProyecto(proyectoId)
        
        // Calcular estadísticas basadas en las actividades reales
        this.calcularEstadisticas()
        
        // Procesar observaciones de actividades
        this.procesarObservacionesActividades()
        
        // Cargar documentos de todas las actividades del proyecto
        await this.cargarDocumentosProyecto()
      } catch (error) {
        console.error('Error al cargar el proyecto:', error)
        this.$toast({
          component: {
            template: '<div>Error al cargar el proyecto</div>',
          },
          props: {
            variant: 'danger',
          },
        })
      } finally {
        this.cargando = false
      }
    },
    
    calcularEstadisticas() {
      if (this.actividades && this.actividades.length > 0) {
        this.estadisticas.actividadesCompletadas = this.actividades.filter(
          actividad => actividad.estadoActividad?.nombre === 'Completado'
        ).length
        
        this.estadisticas.actividadesPendientes = this.actividades.filter(
          actividad => actividad.estadoActividad?.nombre === 'Pendiente'
        ).length
        
        // Calcular días restantes basado en la fecha de fin del proyecto
        if (this.proyecto.fechaFin) {
          const fechaFin = new Date(this.proyecto.fechaFin)
          const hoy = new Date()
          const diferencia = fechaFin.getTime() - hoy.getTime()
          this.estadisticas.diasRestantes = Math.ceil(diferencia / (1000 * 3600 * 24))
        }
      }
    },
    
    procesarObservacionesActividades() {
      if (this.actividades && this.actividades.length > 0) {
        this.observacionesActividades = this.actividades
          .filter(actividad => actividad.observaciones && actividad.observaciones.trim() !== '')
          .map(actividad => ({
            id: actividad.idActividad,
            tipoActividad: actividad.tipoActividad?.nombre || 'Sin tipo',
            observaciones: actividad.observaciones,
            fecha: actividad.fecha,
            estado: actividad.estadoActividad?.nombre || 'Sin estado'
          }))
          .sort((a, b) => new Date(b.fecha) - new Date(a.fecha)) // Ordenar por fecha descendente
      } else {
        this.observacionesActividades = []
      }
    },
    
    calcularProgreso() {
      if (this.actividades && this.actividades.length > 0) {
        const totalActividades = this.actividades.length
        const actividadesCompletadas = this.actividades.filter(
          actividad => actividad.estadoActividad?.nombre === 'Completado'
        ).length
        return Math.round((actividadesCompletadas / totalActividades) * 100)
      }
      return 0
    },
    getEstadoVariant(estado) {
      if (!estado) return 'secondary'
      
      const variants = {
        'En Progreso': 'primary',
        'Completado': 'success',
        'Pendiente': 'warning',
        'Cancelado': 'danger',
        'Activo': 'primary',
        'Inactivo': 'secondary',
        'Finalizado': 'success',
        'En Curso': 'primary',
        'Pausado': 'warning',
        'Terminado': 'success',
      }
      return variants[estado] || 'secondary'
    },
    getEstadoObraVariant(estado) {
      if (!estado) return 'secondary'
      
      const variants = {
        'En Progreso': 'primary',
        'Completado': 'success',
        'Pendiente': 'warning',
        'Cancelado': 'danger',
        'Activo': 'primary',
        'Inactivo': 'secondary',
        'Finalizado': 'success',
        'En Curso': 'primary',
        'Pausado': 'warning',
        'Terminado': 'success',
        'Planificado': 'info',
        'En Construcción': 'primary',
        'Paralizado': 'warning',
        'Abandonado': 'danger',
      }
      return variants[estado] || 'secondary'
    },
    formatearFecha(fecha) {
      if (!fecha) return 'N/A'
      return new Date(fecha).toLocaleDateString('es-ES')
    },
    formatearMoneda(cantidad) {
      if (!cantidad) return 'N/A'
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
      }).format(cantidad)
    },
    verActividad(id) {
      this.$router.push({ name: 'actividades-detalle', params: { id } })
    },
    verObra(id) {
      this.$router.push({ name: 'obras-detalle', params: { id } })
    },
    editarProyecto() {
      this.$router.push({ name: 'proyectos-editar', params: { id: this.proyecto.idProyecto } })
    },
    volver() {
      this.$router.push({ name: 'proyectos-lista' })
    },
    async descargarArchivo(id) {
      try {
        this.$toast({
          message: 'Descargando archivo...',
          variant: 'info',
        })
        
        await DocumentoService.downloadDocumento(id)
        
        this.$toast({
          message: 'Archivo descargado correctamente',
          variant: 'success',
        })
      } catch (error) {
        console.error('Error al descargar archivo:', error)
        this.$toast({
          message: 'Error al descargar el archivo',
          variant: 'danger',
        })
      }
    },
    agregarRecordatorio(actividad) {
      const evento = crearEventoDesdeActividad(actividad)
      
      this.$swal({
        title: 'Agregar Recordatorio',
        text: 'Selecciona cómo quieres agregar el recordatorio:',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Google Calendar',
        cancelButtonText: 'Cancelar',
        showDenyButton: true,
        denyButtonText: 'Outlook',
        showCloseButton: true,
        customClass: {
          confirmButton: 'btn btn-success',
          denyButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          // Google Calendar
          const url = generarEnlaceGoogleCalendar(evento)
          window.open(url, '_blank')
        } else if (result.isDenied) {
          // Outlook
          const url = generarEnlaceOutlookCalendar(evento)
          window.open(url, '_blank')
        }
      })
      
      // Mostrar opciones adicionales
      this.$swal({
        title: 'Otras opciones',
        text: 'También puedes descargar el archivo para otros calendarios:',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Descargar archivo',
        cancelButtonText: 'Cerrar',
        customClass: {
          confirmButton: 'btn btn-info',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          descargarArchivoICS(evento)
        }
      })
    },
    
    async cargarDocumentosProyecto() {
      try {
        // Obtener todos los documentos de las actividades del proyecto
        const todosDocumentos = []
        
        for (const actividad of this.actividades) {
          if (actividad.idActividad) {
            try {
              const documentosActividad = await DocumentoService.getByActividad(actividad.idActividad)
              // Agregar información de la actividad a cada documento
              const documentosConActividad = documentosActividad.map(doc => ({
                ...doc,
                actividadNombre: actividad.tipoActividad?.nombre || 'Actividad',
                actividadFecha: actividad.fecha
              }))
              todosDocumentos.push(...documentosConActividad)
            } catch (error) {
              console.warn(`Error al cargar documentos de actividad ${actividad.idActividad}:`, error)
            }
          }
        }
        
        this.archivos = todosDocumentos
        console.log('Documentos cargados:', this.archivos.length)
      } catch (error) {
        console.error('Error al cargar documentos del proyecto:', error)
        this.archivos = []
      }
    },

    // Métodos para edición de actividades
    async cargarOpcionesSelect() {
      try {
        // Cargar tipos de actividad
        const tiposActividad = await ActividadService.getTiposActividad()
        this.opcionesTipoActividad = [
          { value: null, text: 'Seleccionar tipo' },
          ...tiposActividad.map(tipo => ({
            value: tipo.idTipoActividad,
            text: tipo.nombre
          }))
        ]

        // Cargar estados de actividad
        const estadosActividad = await ActividadService.getEstadosActividad()
        this.opcionesEstadoActividad = [
          { value: null, text: 'Seleccionar estado' },
          ...estadosActividad.map(estado => ({
            value: estado.idEstadoActividad,
            text: estado.nombre
          }))
        ]

        // Cargar tipos de acción
        const tiposAccion = await ActividadService.getTiposAccion()
        this.opcionesAccionActividad = [
          { value: null, text: 'Seleccionar acción' },
          ...tiposAccion.map(tipo => ({
            value: tipo.idAccionActividad,
            text: tipo.nombre
          }))
        ]
      } catch (error) {
        console.error('Error al cargar opciones de select:', error)
      }
    },

    async editarActividad(actividad) {
      this.actividadEditando = actividad
      
      // Cargar datos de la actividad en el modal
      this.modalActividad = {
        tipoActividad: actividad.tipoActividad?.idTipoActividad,
        fecha: actividad.fecha,
        estadoActividad: actividad.estadoActividad?.idEstadoActividad,
        observaciones: actividad.observaciones || '',
        proximaAccionFecha: actividad.proximaAccionFecha || '',
        proximaAccionTipo: actividad.proximaAccionTipo?.idAccionActividad,
        recordatorio: actividad.recordatorio || false,
      }

      // Cargar documentos de la actividad
      try {
        console.log('🔍 Cargando documentos para actividad ID:', actividad.idActividad)
        this.documentosActividad = await DocumentoService.getByActividad(actividad.idActividad)
        console.log('📄 Documentos cargados para edición:', this.documentosActividad.length)
        console.log('📄 Documentos:', this.documentosActividad)
      } catch (error) {
        console.error('❌ Error al cargar documentos de la actividad:', error)
        this.documentosActividad = []
      }

      // Abrir modal
      this.$bvModal.show('modal-editar-actividad')
    },

    async guardarActividadEditada() {
      if (!this.modalActividad.tipoActividad || !this.modalActividad.fecha || !this.modalActividad.estadoActividad) {
        this.$toast.warning('Los campos obligatorios son requeridos')
        return
      }

      this.guardandoActividad = true

      try {
        // Preparar datos de la actividad
        const actividadData = {
          idTipoActividad: this.modalActividad.tipoActividad,
          idEstadoActividad: this.modalActividad.estadoActividad,
          fecha: this.modalActividad.fecha,
          proximaAccionFecha: this.modalActividad.proximaAccionFecha || null,
          idProximaAccionTipo: this.modalActividad.proximaAccionTipo || null,
          observaciones: this.modalActividad.observaciones || '',
          recordatorio: this.modalActividad.recordatorio,
          idCreadoPor: 1, // TODO: Obtener del store de autenticación
          idProyecto: this.proyecto.idProyecto
        }

        // Actualizar actividad en el backend
        const actividadActualizada = await ActividadService.update(this.actividadEditando.idActividad, actividadData)

        // Actualizar la actividad en la lista local
        const index = this.actividades.findIndex(a => a.idActividad === this.actividadEditando.idActividad)
        if (index !== -1) {
          this.actividades[index] = actividadActualizada
        }

        this.$toast.success('Actividad actualizada exitosamente')
        this.$bvModal.hide('modal-editar-actividad')

      } catch (error) {
        console.error('Error al actualizar actividad:', error)
        this.$toast({
          message: 'Error al actualizar la actividad',
          variant: 'danger',
        })
      } finally {
        this.guardandoActividad = false
      }
    },

    cancelarEdicion() {
      this.$bvModal.hide('modal-editar-actividad')
    },

    resetModalActividad() {
      this.modalActividad = {
        tipoActividad: null,
        fecha: '',
        estadoActividad: null,
        observaciones: '',
        proximaAccionFecha: '',
        proximaAccionTipo: null,
        recordatorio: false,
      }
      this.documentosActividad = []
      this.actividadEditando = null
      this.guardandoActividad = false
      this.mostrarSubidaDocumentos = false
    },

    onDocumentoSubido(documento) {
      // Agregar documento a la lista
      this.documentosActividad.push(documento)
      this.$toast.success('Documento agregado correctamente')
      this.mostrarSubidaDocumentos = false
    },

    onFinalizarSubida() {
      this.mostrarSubidaDocumentos = false
    },

    async descargarDocumento(id, nombreArchivo) {
      try {
        this.$toast?.info('Descargando documento...')
        
        // Usar el método del servicio que maneja el nombre original automáticamente
        await DocumentoService.downloadDocumento(id)
        
        this.$toast?.success('Documento descargado correctamente')
      } catch (error) {
        console.error('Error al descargar documento:', error)
        this.$toast?.error('Error al descargar el documento')
      }
    },
    eliminarDocumento(index) {
      this.$swal({
        title: '¿Estás seguro?',
        text: 'Este documento será eliminado permanentemente',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6'
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const documento = this.documentosActividad[index]
            if (documento.idDocumento) {
              await DocumentoService.delete(documento.idDocumento)
            }
            this.documentosActividad.splice(index, 1)
            this.$toast.success('Documento eliminado')
          } catch (error) {
            console.error('Error al eliminar documento:', error)
            this.$toast.error('Error al eliminar el documento')
          }
        }
      })
    },

    resetSubidaDocumentos() {
      this.mostrarSubidaDocumentos = false
    },

    async recargarDocumentos() {
      if (!this.actividadEditando?.idActividad) {
        this.$toast.warning('No hay actividad seleccionada')
        return
      }

      try {
        console.log('🔄 Recargando documentos para actividad:', this.actividadEditando.idActividad)
        this.documentosActividad = await DocumentoService.getByActividad(this.actividadEditando.idActividad)
        console.log('📄 Documentos recargados:', this.documentosActividad.length)
        this.$toast.success(`Documentos recargados: ${this.documentosActividad.length} encontrados`)
      } catch (error) {
        console.error('❌ Error al recargar documentos:', error)
        this.$toast.error('Error al recargar documentos')
      }
    },

  },
}
</script>

<style lang="scss" scoped>
.proyectos-detalle {
  padding: 1.5rem;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.content-header-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.content-header-subtitle {
  color: #6c757d;
  margin-bottom: 0;
}

.info-item {
  margin-bottom: 1rem;
}

.info-item label {
  font-weight: 600;
  color: #495057;
  display: block;
  margin-bottom: 0.25rem;
}

.stats-item {
  text-align: center;
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.stats-item:last-child {
  border-bottom: none;
}

.stats-value {
  font-size: 2rem;
  font-weight: 600;
  color: #007bff;
}

.stats-label {
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.25rem;
}

.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s;
}

.file-item:hover {
  background-color: #f8f9fa;
}

.file-item:last-child {
  border-bottom: none;
}

.file-info {
  flex: 1;
  margin-right: 1rem;
}

.file-name {
  font-weight: 500;
  color: #495057;
  margin-bottom: 0.25rem;
}

.file-meta {
  color: #6c757d;
}

.comment-item {
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.comment-item:last-child {
  border-bottom: none;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.comment-text {
  margin-bottom: 0.5rem;
  color: #495057;
}

.comment-meta {
  margin-top: 0.5rem;
}

@media (max-width: 768px) {
  .content-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .content-header-right {
    margin-top: 1rem;
  }
}

/* Estilos para documentos */
.documento-item {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
}

.documento-item:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

.documento-info {
  flex: 1;
}

.documento-nombre {
  font-weight: 500;
  color: #495057;
  display: flex;
  align-items: center;
}
</style>
